import { FC, useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Logo } from "../Logo";
import HeaderThemeSwitcher from "./HeaderThemeSwitcher";
import HeaderNotifications from "./HeaderNotifications";
import HeaderProjects from "./HeaderProjects";
import HeaderUserMenu from "./HeaderUserMenu";
import HeaderSocials from "./HeaderSocials";
import HeaderAdmin from "./HeaderAdmin/HeaderAdmin";
import Icon from "../Icon";

// * Data
import { headerNavList } from "./Header.data";

//* Props
import { HeaderProps } from "./Header.interface";

// * Helpers
import cn from "classnames";
import { useScreenWidth } from "../../hooks/useScreenWidth";

//* Styles
import styles from "./Header.module.css";
import { checkPwa } from "../../utils/isPwa";

const Header: FC<HeaderProps> = ({
  isBackBtn = true,
  returnUrl = "/",
  title = "На главную",
}) => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const { isMobile } = useScreenWidth();
  const [isPwa, setIsPwa] = useState(checkPwa());

  useEffect(() => {
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleChange = (e: MediaQueryListEvent) => setIsPwa(checkPwa());

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      }
    };
  }, []);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = () => {
    if (!deferredPrompt) {
      alert(`
        Ваш браузер не поддерживает автоматическую установку.
        Вы можете добавить этот сайт вручную:
        1. Откройте меню браузера (⋮ или ⚙)
        2. Найдите пункт:
          - "Установить" или
          - "Добавить на главный экран"
        3. Следуйте инструкциям на экране`);
      return;
    }
    deferredPrompt.prompt();
  };

  return (
    <header className={styles.header}>
      {isBackBtn && (
        <Link to={returnUrl} className={styles["header__mobile-back"]}>
          <div className={styles["header__back-arrow"]}>
            <Icon name="arrow-back" />
          </div>

          <span className={styles.header__title}>{title}</span>
        </Link>
      )}

      <div className={styles.header__logo}>
        <Logo />
      </div>

      <nav className={styles.header__nav}>
        {headerNavList.map(({ caption, url }) => (
          <NavLink
            to={url}
            key={url}
            className={({ isActive }) =>
              cn(styles.header__link, {
                [styles.header__link_active]: isActive,
              })
            }
          >
            {caption}
          </NavLink>
        ))}
      </nav>

      <div className={styles.header__actions}>
        <HeaderAdmin />
        <HeaderThemeSwitcher />
        <HeaderNotifications />

        <div className={styles.header__projects}>
          <HeaderProjects />
        </div>

        {!isPwa && isMobile && (
          <div onClick={handleInstallClick} className={styles.header__install}>
            <Icon name="install" />
          </div>
        )}

        <HeaderUserMenu />

        <div className={styles.header__socials}>
          <HeaderSocials />
        </div>
      </div>
    </header>
  );
};

export default Header;
